import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import dayjs from 'dayjs';
import {
  Input,
  Table,
  Select,
  DatePicker,
  Popover,
  Drawer,
  Collapse,
  Spin,
  Tag,
  Switch,
  Menu,
  Dropdown,
  Avatar,
  notification,
  Tooltip,
  Button,
  Card,
  Popconfirm,
  Modal
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';
import { timezoneAtom } from '../../../../atoms/Atoms';
import { dateRangePresets } from '../../../utils/utils';
import bookingApi from '../../../../api/bookingApi';
import envConfig from '../../../../envConfig';
import { formatPhoneNumber } from '../../../utils/functions';
import { configSelector } from '../../../../atoms/Selectors';
import api from '../../../../api/api';

function SubscriptionGifts() {
  const config = useRecoilValueLoadable(configSelector);
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const { t, i18n } = useTranslation();
  const [dates, setDates] = useState(null);
  const [loading, setLoading] = useState(true);
  const timezone = useRecoilValue(timezoneAtom);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [size, setSize] = useState(20);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationFilter, setLocationFilter] = useState(null);
  const [locations, setLocations] = useState([]);
  const [total, setTotal] = useState(0);
  const [gifts, setGifts] = useState([]);
  const [filter, setFilter] = useState(-1);
  const [selectedGift, setSelectedGift] = useState(null);
  const [userSearchText, setUserSearchText] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function searchUsers() {
    setLoadingUsers(true);
    api
      .post('users/search', { SearchText: userSearchText })
      .then((response) => {
        setLoadingUsers(false);
        if (response?.data?.success) {
          setUsers(response.data.data.results);
        } else {
          showMessage(response?.data?.error);
        }
      })
      .catch((error) => {
        setLoadingUsers(false);
        showMessage(error.message);
      });
  }

  function getLocations() {
    setLoadingLocations(true);
    bookingApi
      .get('locations')
      .then((response) => {
        setLoadingLocations(false);
        setLocations(response.data.data);
      })
      .catch((error) => {
        setLoadingLocations(false);
        showMessage(error.message);
      });
  }

  function getData(page, query) {
    setSearchText(query);
    setLoading(true);
    const payload = {
      Status: filter >= 0 ? filter : null,
      SearchTerm: query,
      Page: page,
      Size: size,
      LocationId: locationFilter,
      StartDate: moment(dates?.start).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000'),
      EndDate: moment(dates?.end).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').add(1, 'days').format('YYYY-MM-DDTHH:mm:00.000'),
    };
    bookingApi
      .post('subscriptions/gifts/search', payload)
      .then((response) => {
        setLoading(false);
        if (!response.data.success) {
          showMessage(response.data.message);
        } else {
          setGifts(response.data.data.gifts);
          setTotal(response.data.data.total);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error.message);
      });
  }

  function assignMembership() {
    setLoading(true);
    bookingApi.post('subscriptions/gifts/assign', {
      RecipientUserId: selectedUser.id,
      GiftId: selectedGift.id
    })
      .then((response) => {
        setLoading(false);
        if (!response.data.success) {
          showMessage(response.data.message);
        } else {
          getData(currentPage, searchText);
          setSelectedGift(null);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error.message);
      });
  }

  useEffect(() => {
    if (dates) {
      getData(0, '');
    }
  }, dates);

  useEffect(() => {
    setCurrentPage(0);
    getData(0, searchText);
  }, [size]);

  useEffect(() => {
    setSelectedUser(null);
    setUserSearchText('');
  }, [selectedGift]);

  useEffect(() => {
    setDates({
      start: moment().startOf('month'),
      end: moment().endOf('month'),
    });
    getLocations();
  }, []);

  return (
    <>
      <PageHeader
        className="mb-4 px-0"
        title={t('membership_gifts')}
      />
      <div className="flex space-x-2">
        <div>
          <strong>{t('location')}:</strong>
          <br />
          <Select
            loading={loadingLocations}
            disabled={loadingLocations || loading}
            style={{ width: '140px' }}
            value={locationFilter}
            onChange={(value) => setLocationFilter(value)}
          >
            <Option
              key="any"
              value={null}
              className="text-xs flex items-center"
            >
              {t('any')}
            </Option>
            {_.map(locations, (location) => (
              <Option
                key={location.id}
                value={location.id}
                className="text-xs flex items-center"
              >
                {i18n.language === 'en' ? location.nameEn : location.nameEs}
              </Option>
            ))}
          </Select>
        </div>
        <div>
          <strong>{t('dates')}:</strong>
          <RangePicker
            disabled={loading}
            format="MMM, DD YYYY"
            className="w-full"
            allowClear={false}
            presets={dateRangePresets(t)}
            value={[
              dayjs(dates?.start),
              dayjs(dates?.end)
            ]}
            onChange={(date, dateString) => {
              if (dateString[0].length > 0 && dateString[0].length > 0) {
                setDates({
                  start: dateString[0],
                  end: dateString[1]
                });
              }
            }}
          />
        </div>
        <div>
          <strong>{t('filter')}:</strong>
          <br />
          <Select
            disabled={loading}
            style={{ width: '140px' }}
            value={filter}
            onChange={(value) => setFilter(value)}
          >
            <Option
              key="any"
              value={-1}
              className="text-xs flex items-center"
            >
              {t('any')}
            </Option>
            <Option
              key="pending"
              value={0}
              className="text-xs flex items-center"
            >
              {t('pending')}
            </Option>
            <Option
              key="assigned"
              value={1}
              className="text-xs flex items-center"
            >
              {t('assigned')}
            </Option>
            <Option
              key="failed"
              value={2}
              className="text-xs flex items-center"
            >
              {t('failed')}
            </Option>
          </Select>
        </div>
        <Input.Search
          className="w-full mb-1 mt-5"
          key="userSubscriptionSearch"
          allowClear
          loading={loading}
          enterButton={t('search')}
          onSearch={(value) => {
            setCurrentPage(0);
            setSearchText(value);
            getData(0, value);
          }}
        />
      </div>
      <Table
        className="mt-2"
        bordered
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: size,
          showSizeChanger: true,
          defaultCurrent: 0,
          current: currentPage + 1,
          total,
          onChange: (page, pageSize) => {
            if (page - 1 !== currentPage) {
              setCurrentPage(page - 1);
              getData(page - 1, searchText);
            } else {
              setSize(pageSize);
            }
          }
        }}
        columns={[
          {
            title: t('purchased_on'),
            key: 'createdAt',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div className="flex flex-col">
                <span>{moment(row.createdAt).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('ddd')}</span>
                <span>{moment(row.createdAt).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('MMM D')}</span>
                <span>{moment(row.createdAt).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('h:mm A')}</span>
              </div>
            ),
          },
          {
            title: t('buyer'),
            key: 'buyer',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div className="flex space-x-2">
                {row.buyer.avatar && row.buyer.avatar.length === 0 && (
                  <Avatar
                    style={{ background: config?.contents?.data?.primaryColor }}
                  >
                    {`${row.buyer.firstName[0]}${row.buyer.lastName[0]}`}
                  </Avatar>
                )}
                {row.buyer.avatar && row.buyer.avatar.length > 0 && (
                  <Avatar
                    style={{ background: config?.contents?.data?.primaryColor }}
                    src={row.buyer.avatar}
                  />
                )}
                <div className="text-left ml-2">
                  <div><span className="mt-1">{row.buyer.completeName}</span></div>
                  <div><span className="mt-1">{row.buyer.email}</span></div>
                  <div><span className="mt-1">{formatPhoneNumber(row.buyer.phoneNumber)}</span></div>
                </div>
              </div>
            ),
          },
          {
            title: t('recipient'),
            key: 'recipient',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div className="flex flex-col">
                <div className="text-left ml-2">
                  <div><span className="mt-1">{row.recipientFullName}</span></div>
                  <div><span className="mt-1">{row.recipientEmail}</span></div>
                  {row.recipientPhone && (
                    <div><span className="mt-1">{formatPhoneNumber(row.recipientPhone)}</span></div>
                  )}
                </div>
              </div>
            ),
          },
          {
            title: t('subscription'),
            key: 'subscription',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <div>
                <div>
                  <span>{i18n.language === 'en' ? row.subscription?.nameEn : row.subscription?.nameEs}</span>
                </div>
                <div className="font-xs text-gray-700 italic">{row.subscription?.internalName}</div>
              </div>
            ),
          },
          {
            title: t('status'),
            key: 'status',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <>
                {row.status === 0 && (
                  <Tag>{t('pending')}</Tag>
                )}
                {row.status === 1 && (
                  <Tag color="green">{t('assigned')}</Tag>
                )}
                {row.status === 2 && (
                  <Tag color="red">{t('failed')}</Tag>
                )}
              </>
            ),
          },
          {
            title: t('actions'),
            key: 'actions',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <Button
                size="small"
                disabled={row.assignedAt}
                type="primary"
                onClick={() => setSelectedGift}
              >
                {t('assign')}
              </Button>
            ),
          }
        ]}
        dataSource={gifts}
      />
      <Modal
        title={t('assign_membership')}
        open={selectedGift}
        onCancel={() => setSelectedGift(null)}
        footer={[
          <Button
            key="close"
            type="primary"
            danger
            loading={loading}
            onClick={() => setSelectedGift(null)}
          >
            {t('close')}
          </Button>,
          <Popconfirm
            title={t('are_you_sure_assign_membership')}
            okText={t('yes')}
            disabled={selectedUser === null}
            cancelText={t('no')}
            onConfirm={() => assignMembership()}
          >
            <Button
              key="ok"
              type="primary"
              loading={loading}
              disabled={selectedUser === null}
            >
              {t('assign')}
            </Button>
          </Popconfirm>
        ]}
      >
        <div>
          <strong>{t('user')}</strong>
          {!selectedUser && (
            <Dropdown
              trigger="click"
              placement="bottom"
              overlay={
                <Menu>
                  {_.map(users, (u) => (
                    <Menu.Item
                      key={u.id}
                      onClick={() => {
                        setSelectedUser(u);
                        setUsers([]);
                        setUserSearchText('');
                      }}
                    >
                      {u.completeName}: {u.email}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <Input.Search
                className="w-full"
                allowClear
                loading={loadingUsers}
                disabled={loading}
                value={userSearchText}
                onChange={(obj) => {
                  setUserSearchText(obj.target.value);
                }}
                onSearch={() => searchUsers()}
                enterButton={t('search')}
              />
            </Dropdown>
          )}
          {selectedUser && (
            <div className="flex mt-1 space-x-2 w-full">
              {selectedUser?.avatar && selectedUser?.avatar.length === 0 && (
                <Avatar
                  style={{ background: config?.contents?.data?.primaryColor }}
                >
                  {`${selectedUser?.firstName[0]}${selectedUser?.lastName[0]}`}
                </Avatar>
              )}
              {selectedUser?.avatar && selectedUser?.avatar.length > 0 && (
                <Avatar
                  style={{ background: config?.contents?.data?.primaryColor }}
                  src={selectedUser?.avatar}
                />
              )}
              <span className="mt-1">{selectedUser?.completeName}</span>
              <Button
                size="small"
                className="mt-1"
                danger
                onClick={() => {
                  setSelectedUser(null);
                }}
              >
                {t('remove')}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

export default SubscriptionGifts;
